import React, { useCallback, useMemo } from 'react';
import produce from 'immer';
import { gql } from '@soundxyz/gql-string';
import type { ShareModalSource } from '@soundxyz/utils/analytics';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { FeedPostReplyModal } from '@components/modals/FeedPostCommentModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalType, useModal } from '@context/modal';
import { useToast } from '@context/toast';
import {
  CurrentUserPostVoteStatus,
  FeedPostActionsInfoFragmentDoc,
  PostVoteChange,
  setQueryData,
  useQuery,
  UserVoteOnPostDocument,
} from '@graphql/reactQuery';
import {
  type FragmentType,
  getFragment,
  useMutation,
  VoteOnPostDocument,
} from '@graphql/reactQuery';
import { useIsHydrated } from '@hooks/useIsHydrated';
import { createBatchingInputStore } from '@store/batchInput';
import { CommentButton } from './CommentButton';
import { SharePostButton } from './SharePostButton';
import { UpvoteButton } from './UpvoteButton';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation VoteOnPost($input: SetVoteOnPostInput!) {
    voteOnPostRL(input: $input) {
      __typename
      ... on RateLimitError {
        message
      }
    }
  }

  query UserVoteOnPost($postIds: [UUID!]!) {
    userVoteOnPost(input: { postIds: $postIds }) {
      postId
      voteStatus
      upvoteCount
    }
  }

  fragment FeedPostActionsInfo on Post {
    id
    commentCount
    upvoteCount
    webappUri
    ...SharePostInfo
    ...FeedPostCommentModal
  }
`);

const { batchedKeys, useBatchedKey } = createBatchingInputStore({
  chunkLimit: 50,
});

export function setVoteStatus({
  postId,
  voteStatus,
  upvoteCount,
}: {
  postId: string;
  voteStatus: CurrentUserPostVoteStatus;
  upvoteCount: number;
}) {
  const postIds = batchedKeys.keys.get(postId);

  if (!postIds) {
    return;
  }

  setQueryData(
    {
      query: UserVoteOnPostDocument,
      variables: {
        postIds,
      },
    },
    previousData => {
      if (!previousData) return previousData;

      return produce(previousData, draft => {
        for (const value of draft.data.userVoteOnPost) {
          if (value.postId === postId) {
            value.voteStatus = voteStatus;
            value.upvoteCount = upvoteCount;
            return;
          }
        }

        draft.data.userVoteOnPost.push({
          voteStatus,
          upvoteCount,
          postId,
        });
      });
    },
  );
}

export enum CommentButtonAction {
  ShowModal,
  FocusInput,
}

export function PostActionButtons({
  postFrag,
  commentButtonAction,
  sourceLocation,
  releasePostArtistTwitterHandle,
}: {
  postFrag: FragmentType<FeedPostActionsInfoFragmentDoc>;
  commentButtonAction: CommentButtonAction;
  sourceLocation: ShareModalSource;
  releasePostArtistTwitterHandle: string | null;
}) {
  const post = getFragment(FeedPostActionsInfoFragmentDoc, postFrag);

  const { id: postId, commentCount } = post;

  const postIds = useBatchedKey({ key: postId });
  const { authToken } = useAuth(); // prefer authtoken so I don't have to wait a render
  const { login } = useConnectedAuth();
  const hydrated = useIsHydrated();
  const { openModal } = useModal();
  const { openToast } = useToast();

  const { data: voteData } = useQuery(UserVoteOnPostDocument, {
    variables: !!postIds && {
      postIds,
    },
    cacheTime: Infinity,
    select({ data }) {
      for (const statusData of data.userVoteOnPost) {
        if (statusData.postId === postId)
          return { voteStatus: statusData.voteStatus, upvoteCount: statusData.upvoteCount };
      }
      return;
    },
    enabled: Boolean(authToken) && !!postIds?.length,
    keepPreviousData: true,
    staleTime: 0,
  });

  const onCommentButtonClick = useCallback(() => {
    if (!authToken) {
      login();
      return;
    }
    if (commentButtonAction === CommentButtonAction.ShowModal) {
      openModal(ModalType.FEED_POST_COMMENT, {
        body: <FeedPostReplyModal key={`feed-post-comment-modal-${post.id}`} postFrag={post} />,
      });
    } else {
      const commentInput = document.querySelector('.comment-input') as HTMLElement;
      if (commentInput) {
        commentInput.focus();
      }
    }
  }, [openModal, commentButtonAction, post, authToken, login]);

  const { mutateAsync, isLoading: isVoting } = useMutation(VoteOnPostDocument, {});

  const handleUpvoteClicked = useStableCallback(async () => {
    if (!authToken) {
      login();
      return;
    }

    if (isVoting || !voteData) return;
    const { voteStatus, upvoteCount } = voteData;

    let mutateResponse: Awaited<ReturnType<typeof mutateAsync>> | undefined;
    if (voteStatus === CurrentUserPostVoteStatus.NoVote) {
      setVoteStatus({
        postId,
        voteStatus: CurrentUserPostVoteStatus.Upvoted,
        upvoteCount: upvoteCount + 1,
      });
      mutateResponse = await mutateAsync({ input: { postId, voteStatus: PostVoteChange.Upvote } });
    } else if (voteStatus === CurrentUserPostVoteStatus.Upvoted) {
      setVoteStatus({
        postId,
        voteStatus: CurrentUserPostVoteStatus.NoVote,
        upvoteCount: upvoteCount - 1,
      });
      mutateResponse = await mutateAsync({
        input: { postId, voteStatus: PostVoteChange.RemoveVote },
      });
    }
    if (mutateResponse?.data.voteOnPostRL?.__typename === 'RateLimitError') {
      openToast({ variant: 'error', text: mutateResponse.data.voteOnPostRL.message });
    }
  });

  /**
   * Only use the votedata upvote count if the user is logged in
   */
  const upvoteCount = useMemo(() => {
    if (authToken && voteData) {
      return voteData.upvoteCount;
    } else {
      return post.upvoteCount;
    }
  }, [voteData, authToken, post]);

  const isLoading = useMemo(() => {
    if (authToken) {
      return !Boolean(voteData);
    } else {
      return !hydrated;
    }
  }, [authToken, hydrated, voteData]);

  return isLoading ? (
    <div className="-my-1 h-7 w-36 animate-pulse rounded-md bg-neutral200" />
  ) : (
    <div className="-mx-2 -my-1 flex h-7 items-center gap-3" data-feed-clickable-div>
      {/**
       * Upvote button
       */}
      <UpvoteButton
        disabled={isVoting}
        onClick={handleUpvoteClicked}
        upvoteCount={upvoteCount}
        voteStatus={voteData?.voteStatus}
        variant="post"
      />

      {/**
       * Comment button
       */}
      <CommentButton onClick={onCommentButtonClick} commentCount={commentCount} variant="post" />

      <SharePostButton
        post={post}
        sourceLocation={sourceLocation}
        releasePostArtistTwitterHandle={releasePostArtistTwitterHandle}
      />
    </div>
  );
}
